import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import {
  Search,
  Filter,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  Link as LinkIcon,
  Layers,
  XCircle,
  CheckCircle,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { usePet } from "../../hooks/usePet";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import { useFileUpload } from "../../hooks/useFileUplode";
import AccessoriesForm from "./AccessoriesForm";
import { useAccessories } from "../../hooks/useAccessories";

function AccessoriesList() {
  const { logout } = useAuth();
  const [pagesList, setPagesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPage, setSelectedPage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [contentsList, setContentsList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: 1,
    showing: 10,
    total: 0,
  });

  const queryClient = useQueryClient();
  const path = useResolvedPath();

  // Assume usePages returns CRUD functions similar to usepages
  // const { fetchPages, createPage, deletePage, updatePage } = usePages({
  //   pagination: { page: pageData.currentPage, limit: 10 },
  //   sort: { field: "created_at", direction: "desc" },
  // });

  const {
    fetchAccessories,
    createAccessories,
    updateAccessories,
    deleteAccessories,
    toggleAccessoriesStatus,

  } = useAccessories();
  const { fileUpload } = useFileUpload();

  // Fetch paginated pages
  // const pagesQuery = useQuery({
  //   queryKey: ["pages", pageData.currentPage],
  //   queryFn: fetchPages,
  //   keepPreviousData: true,
  // });

  const contentsQuery = useQuery({
    queryKey: ["accessories", pageData.currentPage],
    queryFn: () =>
      fetchAccessories({
        pagination: { page: pageData.currentPage, limit: pageData.showing },
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!contentsQuery.isLoading && contentsQuery.data) {
      const response = contentsQuery.data;
      setContentsList(response.data?.accessories || []);
      setPageData({
        currentPage: response.data.pagination?.currentPage || 1,
        totalPages: response.data.pagination?.lastPage || 1,
        total: response.data.pagination?.total || 0,
        showing: response.data.pagination?.showing || 10,
      });
    }
  }, [contentsQuery.data, contentsQuery.isLoading]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (page, mode) => {
    setSelectedPage(page);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPage(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const filteredPages = contentsList.filter((page) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (page.title && page.title.toLowerCase().includes(query)) ||
        (page.pet && page.pet.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const handleUpdate = (page) => {
    openModal(page, "edit");
  };

  const handleDelete = {
    onDelete: (page) =>
      mutation.deleteAccessories.mutate(page._id, {
        onSuccess: (data) => {
          toast.success(data.message || "Accessories Deleted");
          setDeleteModal(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete page");
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (page) => {
      setDeleteModal(true);
      setSelectedPage(page);
    },
  };

  const toggleStatus = (page) => {
    mutation.toggleStatus.mutate(page._id, {
      onSuccess: (data) => {
        toast.success(data.message || "Status updated successfully");
      },
      onError: (error) => toast.error(error.message || "Failed to update status"),
    });
  };

  const mutation = {
    createAccessories: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          return updateAccessories(selectedPage._id, body);
        }
        return createAccessories(body);
      },
    }),
    deleteAccessories: useMutation({
      mutationFn: (id) => deleteAccessories(id),
    }),
    toggleStatus: useMutation({
      mutationFn: async (id) => await toggleAccessoriesStatus(id),
    }),
  };

  const handleSubmit = (body) => {
    if (modalMode === "edit") {
      body._id = selectedPage._id;
    }

    mutation.createAccessories.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["accessories"]);
        toast.success(
          data?.data?.message ||
            `Accessories ${modalMode === "edit" ? "Updated" : "Created"} successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save accessories");
      },
    });
  };

  return (
    <div>
      <main className="">
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Accessories</h1>
          <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Accessory
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Table or Skeleton */}
          {contentsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Price
                    </th>
                    {/* <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Path
                    </th> */}
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Image
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Status
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.length > 0 ? (
                    filteredPages.map((page) => (
                      <tr key={page._id} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <Layers className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                      <div className="font-medium text-gray-900">
                        {page.title}
                      </div>
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-3 text-sm text-gray-500">
                  {page.Price}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {page.image ? (
                    <img
                      src={page.image}
                      alt={page.title || "Image"}
                      className="h-10 w-10 object-cover rounded"
                    />
                  ) : (
                    <div className="flex items-center">
                      <LinkIcon className="mr-1 h-4 w-4 text-gray-400" />
                      <span>No Image</span>
                    </div>
                  )}
                </td>
                {/* <td className="px-4 py-3 text-sm text-gray-500">
                  {Array.isArray(page.contents) &&
                  page.contents.length > 0
                    ? page.contents
                        .map((cid) => {
                          return cid ? cid.title : "-";
                        })
                        .join(", ")
                    : "—"}
                </td> */}
                <td className="px-4 py-3">
                  {mutation.toggleStatus.isPending ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <svg
                        className="animate-spin h-4 w-4 text-gray-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                        ></path>
                      </svg>
                      <span className="ml-1">Updating</span>
                    </span>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                        page.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                      onClick={() => {
                        toggleStatus(page);
                      }}
                      title="Toggle Status"
                    >
                      {page.is_active ? (
                        <CheckCircle className="mr-1 h-3 w-3" />
                      ) : (
                        <XCircle className="mr-1 h-3 w-3" />
                      )}
                      {page.is_active ? "Active" : "Inactive"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {moment(page.created_at).format(
                    "MMMM Do YYYY, h:mm:ss a"
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openModal(page, "view")}
                      className="text-gray-400 hover:text-gray-500"
                      title="View Details"
                    >
                      <StickyNoteIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleUpdate(page)}
                      className="text-blue-400 hover:text-blue-500"
                      title="Edit Page"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      className="text-red-400 hover:text-red-500"
                      onClick={() => handleDelete.onOpen(page)}
                      title="Delete Page"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No Accessories found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedPage)}
        title="Page"
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Accessories Details"
                  : modalMode === "edit"
                  ? "Edit Accessories"
                  : "Create Accessories"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <AccessoriesForm
                mode={modalMode}
                defaultValues={
                  selectedPage
                    ? {
                        ...selectedPage,
                        created_at: moment(selectedPage.created_at).format(
                          "YYYY-MM-DD"
                        ),
                        contentIds: selectedPage.contentIds || [],
                      }
                    : {}
                }
                onCancel={closeModal}
                contentsList={contentsList}
                onSubmit={(data) => handleSubmit(data)}
                submissionLoading={mutation.createAccessories.isLoading}
                fileUpload={fileUpload}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccessoriesList;
